import { useState } from 'react';
import { cn } from '../../../../../lib/utils';
import { primaryText, secondaryText } from '../../../../../styling/stylingHelper';
import {
  listAiCheckerEmailAttachments,
  fetchAiCheckerEmailAttachment,
  type AiCheckerEmailAttachment,
  type AICheckerTrace,
} from '../../../../../services/admin/adminService';
import { KV } from '../shared/copyCells';
import type { TFn } from '../types';

// "Attachments" row for the AI Checker email detail: fetches the attachment list from Gmail on
// demand (nothing stored), then opens each file in a new tab via a blob URL — no manual Gmail lookup.
function AttachmentsRow({ account, gmailMessageId, t }: { account: string; gmailMessageId: string; t: TFn }) {
  const [attachments, setAttachments] = useState<AiCheckerEmailAttachment[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [openingIndex, setOpeningIndex] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    if (loading) return;
    setLoading(true);
    setError(null);
    try {
      setAttachments(await listAiCheckerEmailAttachments(account, gmailMessageId));
    } catch (err: any) {
      console.error('Failed to list email attachments', err);
      setError(err.message || t('adminTesting.review.attachmentsFailed'));
    } finally {
      setLoading(false);
    }
  };

  const open = async (att: AiCheckerEmailAttachment) => {
    if (openingIndex !== null) return;
    setOpeningIndex(att.index);
    setError(null);
    // Open the tab synchronously (before the await) so popup blockers allow it.
    const win = window.open('about:blank', '_blank');
    try {
      const blob = await fetchAiCheckerEmailAttachment(account, gmailMessageId, att.index);
      const url = URL.createObjectURL(blob);
      if (win) win.location.href = url; else window.open(url, '_blank');
    } catch (err: any) {
      console.error('Failed to open email attachment', err);
      win?.close();
      setError(err.message || t('adminTesting.review.attachmentsFailed'));
    } finally {
      setOpeningIndex(null);
    }
  };

  return (
    <div className="flex justify-between gap-2">
      <span className={secondaryText}>{t('adminTesting.review.labelAttachments')}</span>
      <span className="text-right">
        {attachments === null ? (
          <button
            onClick={load}
            disabled={loading}
            className="text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50"
          >
            {loading ? '…' : t('adminTesting.review.attachmentsLoad')}
          </button>
        ) : attachments.length === 0 ? (
          <span className={secondaryText}>{t('adminTesting.review.attachmentsNone')}</span>
        ) : (
          <span className="flex flex-col items-end gap-0.5">
            {attachments.map((att) => (
              <button
                key={att.index}
                onClick={() => open(att)}
                disabled={openingIndex !== null}
                title={att.mimeType}
                className="text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50 truncate max-w-[260px]"
              >
                {openingIndex === att.index ? '…' : att.filename}
              </button>
            ))}
          </span>
        )}
        {error && <span className="block text-[10px] text-red-500 dark:text-textRed-dark">{error}</span>}
      </span>
    </div>
  );
}

export function AICheckerEmailBody({ trace, t }: { trace: AICheckerTrace; t: TFn }) {
  return (
    <div className="space-y-1.5 text-xs">
      <KV label={t('adminTesting.review.labelFrom')} value={trace.emailFrom || '-'} />
      <KV label={t('adminTesting.review.labelSubject')} value={trace.emailSubject || '-'} />
      {trace.platform && <KV label={t('adminTesting.review.labelPlatform')} value={trace.platform} />}
      {trace.restaurantName && <KV label={t('adminTesting.review.labelRestaurant')} value={trace.restaurantName} />}
      {trace.storedTotal != null && <KV label={t('adminTesting.review.labelStoredTotal')} value={`€${trace.storedTotal.toFixed(2)}`} />}
      {trace.orderNumbers.length > 0 && <KV label={t('adminTesting.review.labelOrderNumbers')} value={trace.orderNumbers.join(', ')} />}
      {trace.triageReason && <KV label={t('adminTesting.review.labelReason')} value={trace.triageReason} />}
      <AttachmentsRow account={trace.account} gmailMessageId={trace.gmailMessageId} t={t} />

      {trace.emailIssues.length > 0 && (
        <ul className="list-disc pl-5 mt-1 space-y-1 text-red-600 dark:text-red-400">
          {trace.emailIssues.map((iss, i) => <li key={i}>{iss}</li>)}
        </ul>
      )}
      {trace.emailReasoning && (
        <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
          <p className={cn(secondaryText, 'text-[10px] uppercase tracking-wide mb-0.5')}>{t('adminTesting.review.labelVerifyReasoning')}</p>
          <p className={cn(primaryText, 'text-xs whitespace-pre-wrap')}>{trace.emailReasoning}</p>
        </div>
      )}
    </div>
  );
}
